import { useMemo, useState } from 'react';

import { type QuestionAnswerPayload } from '@apps/web/components/phoebe_chat/user_question_model';
import { Input } from '@apps/web/components/ui/input';
import { type UserQuestionPresentation } from '@apps/web/features/phoebe_chat_v3/model/question_presentation';
import { type V3Question } from '@apps/web/features/phoebe_chat_v3/model/transcript';
import { ActionQuestionCard } from '@apps/web/features/phoebe_chat_v3/ui/action_question_card';
import { V3AmbientButton } from '@apps/web/features/phoebe_chat_v3/ui/ambient_components';
import { cn } from '@apps/web/lib/utils';

type Answer = string | Array<string>;

const DESCRIBED_OPTION_CLASS =
    'flex w-full cursor-pointer flex-col items-start gap-0.5 rounded-2xl ' +
    'border px-4 py-3 text-left transition-colors ' +
    'disabled:pointer-events-none disabled:opacity-50';

const DESCRIBED_OPTION_SELECTED_CLASS = 'border-[#5533EE] bg-[#F3F0FE]';

const DESCRIBED_OPTION_IDLE_CLASS =
    'border-[#DBE3E3] bg-white hover:bg-[#F0F5F5]';

function initialAnswers(
    questions: ReadonlyArray<V3Question>,
): Record<string, Answer> {
    return Object.fromEntries(
        questions.map((question) => [
            question.id,
            question.inputType === 'multi_choice' ? [] : '',
        ]),
    );
}

export function V3QuestionForm({
    questions,
    presentation,
    disabled,
    onSubmit,
}: {
    readonly questions: ReadonlyArray<V3Question>;
    readonly presentation: UserQuestionPresentation | null;
    readonly disabled: boolean;
    readonly onSubmit: (payload: QuestionAnswerPayload) => void;
}) {
    const [answers, setAnswers] = useState<Record<string, Answer>>(() =>
        initialAnswers(questions),
    );
    const [otherAnswers, setOtherAnswers] = useState<Record<string, string>>(
        {},
    );
    const canSubmit = useMemo(
        () =>
            questions.every((question) => {
                const answer = answers[question.id];
                if ((otherAnswers[question.id] ?? '').trim().length > 0) {
                    return true;
                }
                return Array.isArray(answer)
                    ? answer.length > 0
                    : typeof answer === 'string' && answer.trim().length > 0;
            }),
        [answers, otherAnswers, questions],
    );

    function submit() {
        const payloadAnswers = questions.map((question) => {
            const answer = answers[question.id] ?? '';
            const otherAnswer = (otherAnswers[question.id] ?? '').trim();
            return {
                question_id: question.id,
                answer:
                    otherAnswer.length === 0
                        ? answer
                        : Array.isArray(answer)
                          ? [...answer, otherAnswer]
                          : otherAnswer,
                skipped: false,
            };
        });
        onSubmit(
            questions.length === 1
                ? { answer: payloadAnswers[0]?.answer ?? '' }
                : { answers: payloadAnswers },
        );
    }

    function isChoiceSelected(question: V3Question, value: string): boolean {
        const current = answers[question.id];
        return Array.isArray(current)
            ? current.includes(value)
            : current === value;
    }

    function selectChoice(question: V3Question, value: string) {
        if (question.inputType !== 'multi_choice') {
            setOtherAnswers((prior) => ({ ...prior, [question.id]: '' }));
            setAnswers((prior) => ({ ...prior, [question.id]: value }));
            return;
        }
        setAnswers((prior) => {
            const priorValue = prior[question.id];
            const selectedValues = Array.isArray(priorValue) ? priorValue : [];
            return {
                ...prior,
                [question.id]: selectedValues.includes(value)
                    ? selectedValues.filter((entry) => entry !== value)
                    : [...selectedValues, value],
            };
        });
    }

    const presentedQuestion = questions[0];
    if (presentation !== null && presentedQuestion !== undefined) {
        return (
            <ActionQuestionCard
                presentation={presentation}
                question={presentedQuestion.prompt}
                disabled={disabled}
                onAction={(answer) => onSubmit({ answer })}
                onCancel={() => onSubmit({ cancelled: true })}
                onMessageEdit={(message) => {
                    onSubmit({ answer: message });
                    return Promise.resolve();
                }}
            />
        );
    }

    if (
        questions.length === 1 &&
        presentedQuestion?.inputType === 'text' &&
        presentedQuestion.choices.length === 0
    ) {
        return (
            <p
                data-testid="v3-composer-question"
                className="mt-3 max-w-[700px] text-sm leading-5 text-[#002429]"
            >
                {presentedQuestion.prompt}
            </p>
        );
    }

    return (
        <div className="mt-3 space-y-3">
            {questions.map((question) => (
                <fieldset key={question.id} className="space-y-2">
                    <legend className="space-y-1.5">
                        {question.header !== null ? (
                            <span className="inline-flex items-center rounded-full bg-[#F0F5F5] px-2.5 py-1 text-[11px] leading-4 font-semibold tracking-wide text-[#617578] uppercase">
                                {question.header}
                            </span>
                        ) : null}
                        <span className="block text-sm leading-5 font-semibold text-[#002429]">
                            {question.prompt}
                        </span>
                    </legend>
                    {question.choices.length > 0 ? (
                        question.choices.some(
                            (choice) => choice.description !== null,
                        ) ? (
                            <div className="flex flex-col gap-2">
                                {question.choices.map((choice) => {
                                    const selected = isChoiceSelected(
                                        question,
                                        choice.value,
                                    );
                                    return (
                                        <button
                                            key={choice.value}
                                            type="button"
                                            aria-pressed={selected}
                                            disabled={disabled}
                                            className={cn(
                                                DESCRIBED_OPTION_CLASS,
                                                selected
                                                    ? DESCRIBED_OPTION_SELECTED_CLASS
                                                    : DESCRIBED_OPTION_IDLE_CLASS,
                                            )}
                                            onClick={() =>
                                                selectChoice(
                                                    question,
                                                    choice.value,
                                                )
                                            }
                                        >
                                            <span className="text-sm leading-5 font-medium text-[#002429]">
                                                {choice.label}
                                            </span>
                                            {choice.description !== null ? (
                                                <span className="text-xs leading-4 text-[#617578]">
                                                    {choice.description}
                                                </span>
                                            ) : null}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2.5">
                                {question.choices.map((choice) => (
                                    <V3AmbientButton
                                        key={choice.value}
                                        type="button"
                                        variant={
                                            isChoiceSelected(
                                                question,
                                                choice.value,
                                            )
                                                ? 'primary'
                                                : 'outline'
                                        }
                                        disabled={disabled}
                                        onClick={() =>
                                            selectChoice(question, choice.value)
                                        }
                                    >
                                        {choice.label}
                                    </V3AmbientButton>
                                ))}
                            </div>
                        )
                    ) : (
                        <Input
                            value={
                                typeof answers[question.id] === 'string'
                                    ? answers[question.id]
                                    : ''
                            }
                            placeholder={question.placeholder ?? undefined}
                            disabled={disabled}
                            onChange={(event) => {
                                setAnswers((prior) => ({
                                    ...prior,
                                    [question.id]: event.currentTarget.value,
                                }));
                            }}
                        />
                    )}
                    {question.choices.length > 0 && question.allowOther ? (
                        <Input
                            aria-label={`${question.prompt} — Other`}
                            value={otherAnswers[question.id] ?? ''}
                            placeholder="Other answer"
                            disabled={disabled}
                            onChange={(event) => {
                                setOtherAnswers((prior) => ({
                                    ...prior,
                                    [question.id]: event.currentTarget.value,
                                }));
                            }}
                        />
                    ) : null}
                </fieldset>
            ))}
            <div className="flex gap-2.5">
                <V3AmbientButton
                    type="button"
                    variant="primary"
                    disabled={disabled || !canSubmit}
                    onClick={submit}
                >
                    Submit
                </V3AmbientButton>
                <V3AmbientButton
                    type="button"
                    variant="ghost"
                    disabled={disabled}
                    onClick={() => onSubmit({ cancelled: true })}
                >
                    Cancel
                </V3AmbientButton>
            </div>
        </div>
    );
}
